import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Login from './Login.jsx';
import PrivateRoute from './PrivateRoute.jsx';
import Dashboard from './Dashboard.jsx';
import Profile from './Profile.jsx';
import ForgotPassword from './ForgotPassword.jsx';
import Sidebar from './components/Sidebar.jsx';
import Projects from './Projects.jsx';
import ProjectDetail from './ProjectDetail.jsx';
import ProjectSynthese from './ProjectSynthese.jsx';
import ProjectReferencePages from './ProjectReferencePages.jsx';
import ProjectSeoTab from './ProjectSeoTab.jsx';
import ProjectPerformanceTab from './ProjectPerformanceTab.jsx';
import ProjectVisibiliteTab from './ProjectVisibiliteTab.jsx';
import ProjectEdit from './ProjectEdit.jsx';
import ProjectSEODetail from './ProjectSEODetail.jsx';
import Documentation from './Documentation.jsx';
import ProjectWizardPage from './pages/ProjectWizardPage.jsx';
import BackgroundOroles from './components/BackgroundOroles.jsx';
import OnboardingBanner from './components/OnboardingBanner.jsx';
import PublicAuditLanding from './PublicAuditLanding.jsx';
import PublicAuditResult from './PublicAuditResult.jsx';
import ProspectsPage from './ProspectsPage.jsx';
import UsersAdminPage from './UsersAdminPage.jsx';
import { isAuthenticated } from './auth';
// import useAuth from './hooks/useAuth'; // Hook supprimé

const isAuditSubdomain = () => typeof window !== 'undefined' && window.location.hostname === 'audit.ascan.io';
const GTM_ID = 'GTM-P9H43QWP';

/** Pages accessibles sans être connecté : login, audit gratuit (landing + résultat) */
function isPageAllowedWithoutAuth(path) {
  const p = path || '/';
  if (['/login', '/forgot-password', '/register'].includes(p)) return true;
  if (p === '/audit-gratuit' || p.startsWith('/audit/')) return true;
  if (isAuditSubdomain() && (p === '/' || p === '')) return true;
  return false;
}

function Layout({ children }) {
  const location = useLocation();
  const path = location.pathname || '/';
  const isPublicAudit =
    path === '/audit-gratuit' ||
    path.startsWith('/audit/') ||
    (isAuditSubdomain() && (path === '/' || path === ''));
  return (
    <div className="relative min-h-screen overflow-hidden">
      <BackgroundOroles variant={isPublicAudit ? 'warm' : undefined} />
      <div className="relative z-10">{children}</div>
    </div>
  );
}

function useConditionalGtm(path) {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const isPublicAuditPath =
      path === '/audit-gratuit' ||
      path.startsWith('/audit/') ||
      (isAuditSubdomain() && (path === '/' || path === ''));

    const scriptId = 'gtm-script-public-audit';
    const noscriptId = 'gtm-noscript-public-audit';
    const existingScript = document.getElementById(scriptId);
    const existingNoscript = document.getElementById(noscriptId);

    if (isPublicAuditPath) {
      if (!existingScript) {
        const script = document.createElement('script');
        script.id = scriptId;
        script.text = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${GTM_ID}');`;
        document.head.appendChild(script);
      }
      if (!existingNoscript) {
        const ns = document.createElement('noscript');
        ns.id = noscriptId;
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`;
        iframe.height = '0';
        iframe.width = '0';
        iframe.style.display = 'none';
        iframe.style.visibility = 'hidden';
        ns.appendChild(iframe);
        document.body.insertBefore(ns, document.body.firstChild);
      }
      return;
    }

    if (existingScript) existingScript.remove();
    if (existingNoscript) existingNoscript.remove();
  }, [path]);
}

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activePage, setActivePage] = useState('projects');
  const path = location.pathname || '/';
  useConditionalGtm(path);

  // Mettre à jour activePage en fonction de l'URL
  useEffect(() => {
    if (path === '/' || path === '/dashboard') {
      setActivePage('dashboard');
    } else if (path.startsWith('/projects')) {
      setActivePage('projects');
    } else if (path === '/docs') {
      setActivePage('docs');
    } else if (path === '/profile') {
      setActivePage('profile');
    } else if (path === '/prospects') {
      setActivePage('prospects');
    } else if (path === '/admin/users') {
      setActivePage('admin-users');
    }
  }, [path]);

  const isAuthPage = ['/login', '/forgot-password'].includes(path);
  const isPublicAuditPath =
    path === '/audit-gratuit' ||
    path.startsWith('/audit/') ||
    (isAuditSubdomain() && (path === '/' || path === ''));
  const isOnAuditHost = useMemo(() => window.location.host === 'audit.ascan.io', []);

  // Non connecté et pas sur une page autorisée sans auth → redirection vers connexion
  if (!isAuthenticated() && !isPageAllowedWithoutAuth(path)) {
    return <Navigate to="/login" replace state={{ from: path }} />;
  }

  // Pages d'audit gratuit (landing + résultat) — sans sidebar
  if (isPublicAuditPath || (isOnAuditHost && (path === '/' || path === ''))) {
    return (
      <Routes>
        <Route path={isOnAuditHost ? '/' : '/audit-gratuit'} element={<PublicAuditLanding />} />
        <Route path="/audit/:token" element={<PublicAuditResult />} />
      </Routes>
    );
  }

  // Pages d'authentification
  if (isAuthPage || path === '/register') {
    return (
      <Routes>
        <Route path="/register" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
      </Routes>
    );
  }

  // Si authentifié, afficher le layout avec sidebar
  return (
    <div className="flex h-screen w-full max-w-full">
      <Sidebar
        active={activePage}
        onNavigate={page => {
          setActivePage(page);
          if (page === 'dashboard') navigate('/');
          else if (page === 'projects') navigate('/projects');
          else if (page === 'docs') navigate('/docs');
          else if (page === 'profile') navigate('/profile');
          else if (page === 'prospects') navigate('/prospects');
          else if (page === 'admin-users') navigate('/admin/users');
        }}
        user={null} // user supprimé avec le hook useAuth
      />
      <main className="flex-1 px-6 py-8 overflow-y-auto w-full max-w-full">
        <OnboardingBanner />
        <Routes>
          <Route path="/projects/:slug" element={<PrivateRoute><ProjectDetail /></PrivateRoute>}>
            <Route index element={<ProjectSynthese />} />
            <Route path="reference-pages" element={<ProjectReferencePages />} />
            <Route path="seo" element={<ProjectSeoTab />} />
            <Route path="internal-links" element={<ProjectSeoTab defaultSub="links" />} />
            <Route path="performance" element={<ProjectPerformanceTab />} />
            <Route path="errors" element={<ProjectPerformanceTab defaultSub="errors" />} />
            <Route path="position" element={<ProjectVisibiliteTab />} />
            <Route path="visual-audit" element={<ProjectVisibiliteTab defaultSub="visual" />} />
            <Route path="edit" element={<ProjectEdit />} />
          </Route>
          <Route path="/projects/:slug/seo/:encodedUrl" element={<PrivateRoute><ProjectSEODetail /></PrivateRoute>} />
          <Route path="/projects/:slug/audit" element={<PrivateRoute><ProjectWizardPage /></PrivateRoute>} />
          <Route path="/projects/create" element={<PrivateRoute><ProjectWizardPage /></PrivateRoute>} />
          <Route path="/projects" element={<PrivateRoute><Projects /></PrivateRoute>} />
          <Route path="/docs" element={<PrivateRoute><Documentation /></PrivateRoute>} />
          <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
          <Route path="/prospects" element={<PrivateRoute><ProspectsPage /></PrivateRoute>} />
          <Route path="/admin/users" element={<PrivateRoute><UsersAdminPage /></PrivateRoute>} />
        </Routes>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <Layout>
      <AppContent />
    </Layout>
  );
}

